import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import Modal from '../ui/Modal';
import customerService, {
  PointPurchaseInit,
  SubscriptionPurchaseInit,
} from '../../services/customerService';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { formatCurrency } from '../../utils/currency';
import { PurchasableProduct } from '../../types/customer';
import SubscriptionTerms from './SubscriptionTerms';
import ProductThumbnail from '../products/ProductThumbnail';
import RichText from '../ui/RichText';
import purchaseErrorMessage from '../../utils/purchaseErrors';
import { describePointsExpiry } from '../../utils/pointsExpiry';
import PurchaseCelebration from './PurchaseCelebration';
import { PASSWORD_MIN_LENGTH } from '../auth/PasswordInput';

/**
 * Buying a point pack OR a subscription plan.
 *
 * The two differ only in what the server creates — a PaymentIntent for a pack,
 * a Stripe Subscription for a plan — and both hand back a clientSecret for the
 * same Elements form. A plan whose first invoice needed no authentication comes
 * back with a null secret and is already active, so there is nothing to confirm.
 *
 * DIRECT CHARGES: the PaymentIntent lives on the HOST's connected account, so
 * stripe.js must be loaded with `stripeAccount` or the PaymentElement fails
 * with "no such payment intent". Same requirement as the booking Pay Now screen.
 *
 * WORKS SIGNED OUT — BUT NEVER ANONYMOUSLY ANY MORE. Someone can still buy
 * before they have an account; what changed is that they do not leave without
 * one. The address they type decides which of three things happens, and the
 * SERVER decides which, never this file:
 *
 *   - it already has a PASSWORD → the purchase is refused with
 *     PASSWORD_REQUIRED and this form asks them to sign in, with a reset link
 *     underneath. Attaching a purchase to an account nobody has proved is
 *     theirs is how you sell someone else's points into someone else's wallet.
 *   - it exists but was never claimed → nothing changes. They buy, and the
 *     emailed claim link is still how they get in. ⚠️ THEY ARE NEVER OFFERED
 *     THE PASSWORD STEP BELOW: that identity may already hold points, and
 *     setting a password on it from a public form is account takeover for the
 *     price of the smallest pack on the shelf.
 *   - it has no account at all → the purchase creates one, the server signs
 *     them in on the spot, and the receipt panel asks for a password before
 *     letting them go. Safe for exactly one reason: an identity this checkout
 *     created holds nothing except what this checkout just paid for.
 *
 * Signed in, the server uses the verified address from the token and ignores
 * anything typed here.
 *
 * The grant is confirmed server-side after Stripe reports success, and Stripe's
 * own webhook confirms independently. Both run the same idempotent code, so the
 * points land exactly once even if the buyer closes the tab mid-redirect.
 */

/**
 * What the payment form actually needs, common to both kinds. The two init
 * responses carry different extras (a pack has points, a plan has a
 * subscription id); narrowing to this keeps the form from caring which it is.
 */
interface CheckoutInit {
  clientSecret: string;
  amount: number;
  currency: string;
  publishableKey: string;
  connectedAccountId: string;
  /** Packs only. */
  pointsGranted?: number;
  /**
   * The witness the server minted for an address with no account, carried
   * through the payment step and handed back at confirm. Kept in state and
   * nowhere else: it belongs to this checkout and must die with it.
   */
  checkoutClaimToken?: string;
}

interface PointPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: PurchasableProduct | null;
  /** Who is selling — named on the subscription confirmation when known. */
  sellerName?: string | null;
  /** Fired after the points are granted, so the caller can refresh a balance. */
  onPurchased?: (points: number, balance: number) => void;
  /**
   * Somewhere to go on from the confirmation — the booking they left to come
   * here. Offered as a BUTTON rather than an automatic redirect: the panel it
   * sits under is the receipt, and whisking someone off it is disorienting when
   * they have just paid.
   */
  continueLabel?: string;
  onContinue?: () => void;
}

const PointPurchaseModal: React.FC<PointPurchaseModalProps> = ({
  isOpen,
  onClose,
  product,
  sellerName,
  onPurchased,
  continueLabel,
  onContinue,
}) => {
  const { t } = useTranslation();
  const { isSignedIn, me, refresh: refreshAuth, signIn } = useCustomerAuth();

  const [email, setEmail] = useState('');
  const [init, setInit] = useState<CheckoutInit | null>(null);
  /** Set when a PLAN was bought — used to sync it after authentication. */
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [planActive, setPlanActive] = useState(false);
  const [starting, setStarting] = useState(false);

  /**
   * The address turned out to have an account already, so they sign in before
   * anything is charged. Set ONLY by the server's PASSWORD_REQUIRED refusal —
   * this form never guesses at who has an account.
   */
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [password, setPassword] = useState('');
  const [resetSent, setResetSent] = useState(false);

  /**
   * This purchase created the account and signed them in, so a password is
   * owed before they leave. Lifted to here because it also has to take the
   * modal's close button away — a step you can dismiss is not a required one.
   */
  const [mustSetPassword, setMustSetPassword] = useState(false);

  const isSubscription = product?.kind === 'subscription';
  /** Null unless this pack actually has a window — most do not. */
  const packExpiry = describePointsExpiry(
    t,
    product?.pointsExpiryValue,
    product?.pointsExpiryUnit
  );
  const [error, setError] = useState<string | null>(null);

  // Reset whenever the modal opens on a different pack.
  useEffect(() => {
    if (!isOpen) {
      setInit(null);
      setSubscriptionId(null);
      setPlanActive(false);
      setError(null);
      setStarting(false);
      setPasswordRequired(false);
      setPassword('');
      setResetSent(false);
      setMustSetPassword(false);
    }
  }, [isOpen]);

  /**
   * The address this purchase is held against.
   *
   * The session's when there is one, otherwise the typed one — and the CHECK
   * below is on the address itself, not on whether we think we are signed in.
   * Those two came apart: a screen that believed it had a session sent a
   * purchase with no address and no token, and the server refused it with a
   * message the buyer could not act on, because the email field is hidden from
   * someone "signed in".
   */
  const sessionEmail = isSignedIn ? (me?.email ?? '').trim() : '';
  const effectiveEmail = sessionEmail || email.trim();
  const hasUsableEmail = effectiveEmail.includes('@');

  /**
   * Send the claim / reset link for someone who cannot remember their password.
   *
   * ⚠️ ALWAYS REPORTS THE SAME THING, whatever happened. The server answers
   * identically for a known and an unknown address, and contradicting it here
   * would turn this button into a way to test addresses. We already had to
   * admit this one has an account to ask for a password at all; that is no
   * reason to leak anything further.
   */
  const sendResetLink = async () => {
    if (!hasUsableEmail) {
      setError(t('booking:purchase.emailRequired', 'Enter the email your points should go to.'));
      return;
    }
    setStarting(true);
    try {
      await customerService.requestPasswordReset(effectiveEmail);
    } catch {
      /* swallowed on purpose — see above */
    } finally {
      setResetSent(true);
      setError(null);
      setStarting(false);
    }
  };

  const begin = async () => {
    if (!product) return;
    if (!hasUsableEmail) {
      setError(t('booking:purchase.emailRequired', 'Enter the email your points should go to.'));
      return;
    }

    setStarting(true);
    setError(null);
    try {
      /**
       * SIGN IN FIRST when the server has told us this address is spoken for.
       *
       * Done here rather than as a separate screen so the buyer never loses
       * the pack they chose. Once it succeeds the session carries the rest of
       * the purchase, and the server stops reading the typed address at all.
       */
      if (passwordRequired) {
        if (!password) {
          setError(
            t('booking:purchase.passwordRequiredHint', 'Enter your password to continue.')
          );
          setStarting(false);
          return;
        }
        try {
          await signIn(effectiveEmail, password);
          setPasswordRequired(false);
          setPassword('');
        } catch {
          setError(
            t('booking:customer.invalidCredentials', 'That email and password did not match.')
          );
          setStarting(false);
          return;
        }
      }

      const isPlan = product.kind === 'subscription';
      /**
       * The address goes with EVERY request, signed in or not. The server
       * prefers the session's verified address and ignores the body's, so this
       * cannot redirect a purchase — it only stops one from arriving with no
       * identity at all when a session turns out to be half gone.
       */
      const result: PointPurchaseInit | SubscriptionPurchaseInit = isPlan
        ? await customerService.purchaseSubscription(product.id, effectiveEmail)
        : await customerService.purchasePointPack(product.id, effectiveEmail);

      if (isPlan) {
        const plan = result as SubscriptionPurchaseInit;
        setSubscriptionId(plan.subscriptionId);
        if (!plan.clientSecret) {
          // No secret means one of two very different things, and treating
          // them alike is what made a failed purchase look like a silent
          // success: the dialog closed on a subscription still awaiting
          // payment. Only Stripe's own status may decide this.
          const live = plan.status === 'active' || plan.status === 'trialing';
          if (live) {
            setPlanActive(true);
            // The plan went live without an authentication step, so this is
            // where a brand-new account gets its session — and therefore where
            // the password becomes due.
            if (plan.signedIn) setMustSetPassword(true);
            await refreshAuth();
            onPurchased?.(0, 0);
            return;
          }
          setError(
            t(
              'booking:purchase.couldNotStart',
              'We could not start the payment. Nothing has been charged — please try again.'
            )
          );
          return;
        }
      }

      if (!result.publishableKey) {
        setError(
          t(
            'booking:purchase.unavailable',
            'Payment is not available right now. Please contact the host.'
          )
        );
        return;
      }
      setInit({
        clientSecret: result.clientSecret as string,
        amount: result.amount,
        currency: result.currency,
        publishableKey: result.publishableKey,
        connectedAccountId: result.connectedAccountId,
        pointsGranted: (result as PointPurchaseInit).pointsGranted,
        checkoutClaimToken: result.checkoutClaimToken,
      });
    } catch (err: any) {
      /**
       * The address already has an account. Not an error to apologise for —
       * it is a step, so the form grows a password field rather than telling
       * them something went wrong.
       */
      if (err?.response?.data?.error === 'PASSWORD_REQUIRED') {
        setPasswordRequired(true);
        setError(purchaseErrorMessage(err, t));
        return;
      }
      setError(purchaseErrorMessage(err, t));
    } finally {
      setStarting(false);
    }
  };

  /**
   * A REQUIRED step is not one you can dismiss.
   *
   * While the password is owed there is no close button and the backdrop does
   * nothing — otherwise "not skippable" means "skippable by pressing Escape",
   * and the buyer ends up with an account they cannot get back into.
   */
  const dismiss = () => {
    if (mustSetPassword) return;
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={dismiss}
      showCloseButton={!mustSetPassword}
      title={t('booking:purchase.title', 'Get points')}
      size="small"
    >
      {!product ? null : planActive ? (
        /* A plan that went live with no authentication step: there is no
           payment form to render, and before this branch existed the dialog
           simply sat on the details panel as though nothing had happened. */
        <PurchaseDone
          isSubscription
          points={null}
          isSignedIn={isSignedIn}
          mustSetPassword={mustSetPassword}
          onPasswordSet={() => setMustSetPassword(false)}
          onClose={onClose}
          continueLabel={continueLabel}
          onContinue={onContinue}
        />
      ) : !init ? (
        <div className="space-y-4">
          {/* What they picked, shown the way it was shown on the shelf — same
              imagery, same ladder. Arriving at a payment screen that looks
              nothing like the thing you chose is how a buyer starts wondering
              whether they picked the right one.

              The thumbnail is CENTRED against the text beside it. Top-aligned,
              a 40px square sat against a three-line block and read as having
              slipped out of place. */}
          <div className="flex items-center gap-3 rounded-md border border-gray-200 p-3">
            <ProductThumbnail product={product} size="sm" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900">{product.title}</p>
              {!isSubscription && (
                <p className="mt-1 text-xs text-gray-500">
                  {t('booking:points.packSize', '{{count}} points', {
                    count: product.pointsGranted ?? 0,
                  })}
                </p>
              )}
              {/* HOW LONG THEY LAST, on the screen that asks for the card.
                  Whether points expire is a term of the sale, and this is the
                  last moment anyone can decline it. Absent — the usual case —
                  when the pack has no window at all. */}
              {!isSubscription && packExpiry && (
                <p className="mt-1 text-xs text-amber-700">{packExpiry}</p>
              )}
              {/* WHAT THEY ARE BUYING, IN THE HOST'S OWN WORDS.
                  The shelf shows this and the payment screen did not, so the
                  one screen asking for a card was the least informative of the
                  lot — and a pack whose title is "test" says nothing at all
                  without it. Same sanitising renderer as the option list, so a
                  host's formatting survives and their markup cannot. */}
              {product.description && (
                <RichText
                  html={product.description}
                  className="mt-1 text-xs text-gray-500"
                />
              )}
              {product.price != null && (
                <p className="mt-2 text-lg font-semibold text-gray-900">
                  {formatCurrency(Number(product.price), product.currency)}
                </p>
              )}
            </div>
          </div>

          {/* Shown whenever the SESSION supplies no address — normally "signed
              out", but also a session that has gone stale underneath us.
              Hiding it in that second case is what turned a fixable refusal
              into a dead end. Keyed on the session rather than on the typed
              value, or the field would vanish mid-word as soon as an "@" was
              entered. */}
          {!sessionEmail && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                {t('common:form.email', 'Email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  /**
                   * A DIFFERENT ADDRESS IS A DIFFERENT QUESTION.
                   *
                   * "This one has an account" was an answer about the address
                   * that was in the box a moment ago. Leaving the password
                   * field up after a correction asks someone to prove they own
                   * an address we have not asked about — and the next press
                   * would try to sign them in to it.
                   */
                  setPasswordRequired(false);
                  setPassword('');
                  setResetSent(false);
                  setError(null);
                }}
                className="input-base w-full"
                autoComplete="email"
                disabled={starting}
              />
              <p className="mt-1 text-xs text-gray-500">
                {t(
                  'booking:purchase.emailHint',
                  'Your points are held against this address. You will set a password for it after paying.'
                )}
              </p>
            </div>
          )}

          {/* Only ever shown because the SERVER refused the purchase for this
              address. Never offered speculatively: a password field on a form
              that does not need one asks everybody to prove something almost
              nobody has. */}
          {passwordRequired && !sessionEmail && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                {t('common:form.password', 'Password')}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-base w-full"
                autoComplete="current-password"
                disabled={starting}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !starting) begin();
                }}
              />
              {resetSent ? (
                <p className="mt-2 text-xs text-gray-600">
                  {t(
                    'booking:customer.resetSent',
                    "If that address is registered, we've sent a link to it."
                  )}
                </p>
              ) : (
                <button
                  type="button"
                  className="btn-text mt-2 text-xs text-primary-600 hover:text-primary-700"
                  onClick={sendResetLink}
                  disabled={starting}
                >
                  {t('booking:customer.forgotPassword', 'Forgot your password?')}
                </button>
              )}
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={starting}>
              {t('common:actions.cancel', 'Cancel')}
            </button>
            <button type="button" className="btn-primary" onClick={begin} disabled={starting}>
              {starting
                ? t('common:status.loading', 'Loading...')
                : passwordRequired
                  ? t('booking:customer.signIn', 'Sign in')
                  : t('booking:purchase.continue', 'Continue to payment')}
            </button>
          </div>

          {/* Below the button, in the same grey as any other fine print. A
              recurring purchase carries statutory disclosure in Japan; a
              one-off pack has nothing to disclose. */}
          {isSubscription && <SubscriptionTerms product={product} sellerName={sellerName} />}
        </div>
      ) : (
        <PointPaymentForm
          init={init}
          subscriptionId={subscriptionId}
          isSignedIn={isSignedIn}
          mustSetPassword={mustSetPassword}
          onMustSetPassword={() => setMustSetPassword(true)}
          onPasswordSet={() => setMustSetPassword(false)}
          onClose={onClose}
          onPurchased={onPurchased}
          continueLabel={continueLabel}
          onContinue={onContinue}
        />
      )}
    </Modal>
  );
};

interface PaymentFormProps {
  init: CheckoutInit;
  /** Non-null when a PLAN is being paid for rather than a pack. */
  subscriptionId: string | null;
  isSignedIn: boolean;
  /** Owned by the modal, because it also removes the way out of it. */
  mustSetPassword: boolean;
  onMustSetPassword: () => void;
  onPasswordSet: () => void;
  onClose: () => void;
  onPurchased?: (points: number, balance: number) => void;
  continueLabel?: string;
  onContinue?: () => void;
}

/** Elements wrapper — needs the connected account to resolve the intent. */
const PointPaymentForm: React.FC<PaymentFormProps> = ({
  init,
  subscriptionId,
  isSignedIn,
  mustSetPassword,
  onMustSetPassword,
  onPasswordSet,
  onClose,
  onPurchased,
  continueLabel,
  onContinue,
}) => {
  // Memoized so the stripe.js bundle is not re-loaded on every render.
  const stripePromise = useMemo<Promise<Stripe | null>>(
    () => loadStripe(init.publishableKey, { stripeAccount: init.connectedAccountId }),
    [init.publishableKey, init.connectedAccountId]
  );

  return (
    <Elements
      stripe={stripePromise}
      options={{ clientSecret: init.clientSecret, appearance: { theme: 'stripe' as const } }}
    >
      <PointPaymentInner
        init={init}
        subscriptionId={subscriptionId}
        isSignedIn={isSignedIn}
        mustSetPassword={mustSetPassword}
        onMustSetPassword={onMustSetPassword}
        onPasswordSet={onPasswordSet}
        onClose={onClose}
        onPurchased={onPurchased}
        continueLabel={continueLabel}
        onContinue={onContinue}
      />
    </Elements>
  );
};

const PointPaymentInner: React.FC<PaymentFormProps> = ({
  init,
  subscriptionId,
  isSignedIn,
  mustSetPassword,
  onMustSetPassword,
  onPasswordSet,
  onClose,
  onPurchased,
  continueLabel,
  onContinue,
}) => {
  const { t } = useTranslation();
  const { refresh } = useCustomerAuth();
  const stripe = useStripe();
  const elements = useElements();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [granted, setGranted] = useState<{ points: number; balance: number } | null>(null);

  const pay = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);

    // `redirect: 'if_required'` keeps card payments in-page; methods that
    // genuinely need a redirect still get one, and the webhook grants the
    // points when the buyer does not make it back.
    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (stripeError) {
      setError(stripeError.message || t('booking:purchase.failed', 'Payment failed.'));
      setSubmitting(false);
      return;
    }

    if (paymentIntent?.status !== 'succeeded') {
      // Still processing. The webhook will grant when it settles, so this is not
      // an error — just not something to celebrate yet.
      setError(
        t(
          'booking:purchase.processing',
          'Your payment is processing. Your points will appear shortly.'
        )
      );
      setSubmitting(false);
      return;
    }

    try {
      if (subscriptionId) {
        // A PLAN: nothing is granted, the subscription simply becomes active.
        // Syncing now means the portal is right immediately instead of waiting
        // for Stripe's webhook — and it is where the session arrives for a card
        // that needed authentication.
        const result = await customerService.syncSubscription(
          subscriptionId,
          init.checkoutClaimToken
        );
        setGranted({ points: 0, balance: 0 });
        if (result.signedIn) onMustSetPassword();
        await refresh();
        onPurchased?.(0, 0);
      } else {
        const result = await customerService.confirmPointPurchase(
          paymentIntent.id,
          init.checkoutClaimToken
        );
        setGranted({ points: result.points ?? 0, balance: result.balance ?? 0 });
        // The purchase created the account and signed them in, so the password
        // is now owed before they go anywhere.
        if (result.signedIn) onMustSetPassword();
        await refresh();
        onPurchased?.(result.points ?? 0, result.balance ?? 0);
      }
    } catch {
      // The money is taken and the webhook will still grant. Say so rather than
      // implying the payment failed.
      setError(
        t(
          'booking:purchase.confirmLater',
          'Payment received. Your points will appear in a moment.'
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (granted) {
    return (
      <PurchaseDone
        isSubscription={!!subscriptionId}
        points={granted.points}
        isSignedIn={isSignedIn}
        mustSetPassword={mustSetPassword}
        onPasswordSet={onPasswordSet}
        onClose={onClose}
        continueLabel={continueLabel}
        onContinue={onContinue}
      />
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        {subscriptionId
          ? t('booking:purchase.payingPlan', '{{amount}} per billing period', {
              amount: formatCurrency(init.amount, init.currency),
            })
          : t('booking:purchase.paying', '{{points}} points for {{amount}}', {
              points: init.pointsGranted ?? 0,
              amount: formatCurrency(init.amount, init.currency),
            })}
      </p>

      <PaymentElement />

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
          {t('common:actions.cancel', 'Cancel')}
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={pay}
          disabled={!stripe || submitting}
        >
          {submitting
            ? t('common:status.processing', 'Processing...')
            : t('booking:purchase.pay', 'Pay')}
        </button>
      </div>
    </div>
  );
};

/**
 * The receipt — and, for someone whose account this purchase just created, the
 * password that finishes it.
 *
 * ⚠️ THE PASSWORD STEP IS ONLY EVER REACHED ON A BRAND-NEW ACCOUNT. Whether it
 * appears is decided by the SERVER, which issued a session only after checking
 * that the identity did not exist before this checkout began. A buyer whose
 * address already had an account never sees it: setting a password there from
 * a public form would hand over a wallet that was already somebody's.
 *
 * IT CANNOT BE SKIPPED. There is no Close and no Continue until it is done,
 * and the modal has taken its own close button away for the duration. Every
 * escape hatch here is one more person who paid us and then could not get back
 * into the account holding what they paid for.
 */
const PurchaseDone: React.FC<{
  isSubscription: boolean;
  /**
   * How many points this purchase added. Null for a plan, which grants none.
   *
   * ⚠️ THE BALANCE IS NOT PASSED, AND SHOULD NOT BE. It used to read "Added
   * 1000 points. You now have 1000." — the second sentence restates the first
   * for the buyer this screen is usually showing (their first pack), and for
   * everyone else it is an account detail in the middle of a celebration. The
   * running total belongs in the header chip and the history, which is where
   * someone goes to look for it.
   */
  points: number | null;
  isSignedIn: boolean;
  mustSetPassword: boolean;
  onPasswordSet: () => void;
  onClose: () => void;
  continueLabel?: string;
  onContinue?: () => void;
}> = ({
  isSubscription,
  points,
  isSignedIn,
  mustSetPassword,
  onPasswordSet,
  onClose,
  continueLabel,
  onContinue,
}) => {
  const { t } = useTranslation();
  const { refresh } = useCustomerAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    /**
     * CHECK THE LENGTH HERE, TOO.
     *
     * The server is still the authority — it runs `validatePasswordStrength`
     * and refuses anything short. But that endpoint answers with a bare
     * `error` and NO `message`, and its error is sometimes a whole English
     * sentence rather than a code, so a refusal arriving from it can only be
     * rendered as "we could not set that password". On a step nobody is
     * allowed to skip, that is a dead end: blocked, with no way to learn what
     * is wrong. `PASSWORD_MIN_LENGTH` is the same 8 the server enforces and
     * the same constant the host's reset and onboarding screens check against.
     */
    if (password.length < PASSWORD_MIN_LENGTH) {
      setError(
        t(
          'common:auth.passwordRequirements',
          'Use 8+ characters with a mix of letters, numbers & symbols'
        )
      );
      return;
    }
    if (password !== confirmPassword) {
      setError(t('booking:purchase.passwordMismatch', 'Those passwords do not match.'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await customerService.setPassword(password);
      // The session already exists; this only refreshes what the header shows.
      await refresh();
      onPasswordSet();
    } catch (err: any) {
      setError(purchaseErrorMessage(err, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* The picture leads, and the sentence under it is the caption. A green
          bar with a line of text in it is what we show for "settings saved" —
          the wrong register for the one screen in the product where someone has
          just been given something. */}
      <PurchaseCelebration variant={isSubscription ? 'plan' : 'points'} />

      <p className="text-center text-lg font-semibold text-gray-900">
        {isSubscription
          ? t('booking:purchase.planSuccess', 'Your plan is active.')
          : t('booking:purchase.success', 'Added {{points}} points.', {
              points: points ?? 0,
            })}
      </p>

      {mustSetPassword ? (
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium text-gray-900">
              {t('booking:purchase.setPasswordTitle', 'Choose a password')}
            </p>
            <p className="mt-1 text-sm text-gray-600">
              {t(
                'booking:purchase.setPasswordBody',
                'Your account is ready. Set a password so you can sign in and use what you have bought.'
              )}
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t('common:form.password', 'Password')}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-base w-full"
              autoComplete="new-password"
              disabled={saving}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t('booking:purchase.confirmPassword', 'Confirm password')}
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input-base w-full"
              autoComplete="new-password"
              disabled={saving}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !saving && password) save();
              }}
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          {/* One button, and no way past it. */}
          <button
            type="button"
            className="btn-primary w-full"
            onClick={save}
            disabled={saving || !password || !confirmPassword}
          >
            {saving
              ? t('common:status.saving', 'Saving...')
              : t('booking:purchase.setPasswordAction', 'Set password and continue')}
          </button>
        </div>
      ) : (
        <>
          {/* Bought against an address that already had an account they have
              never claimed. Nothing can be set here — the link in the email is
              what proves the address is theirs before a password goes on a
              wallet that may already hold something. */}
          {!isSignedIn && (
            <p className="text-sm text-gray-600">
              {t(
                'booking:purchase.accountEmailSent',
                "We've emailed you a link to finish setting up your account, so you can check this later. You don't need it to carry on now."
              )}
            </p>
          )}

          <div className="flex justify-end gap-3">
            <button type="button" className="btn-secondary" onClick={onClose}>
              {t('common:actions.close', 'Close')}
            </button>
            {onContinue && (
              <button type="button" className="btn-primary" onClick={onContinue}>
                {continueLabel}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PointPurchaseModal;
